import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { 
  Car, Search, Eye, EyeOff, Trash2, TestTube, RefreshCw, 
  Filter, AlertTriangle, User, ExternalLink,
  CheckCircle, XCircle, Crown
} from 'lucide-react';
import { toast } from 'sonner';
import { PlatformOwnerAppShell } from '../components/PlatformOwnerAppShell';

const PlatformOwnerVehicleManagement = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [includeArchivedTest, setIncludeArchivedTest] = useState(false);
  
  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus && filterStatus !== 'all') {
        params.append('filter_status', filterStatus);
      }
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      if (includeArchivedTest) {
        params.append('include_archived_test', 'true');
      }
      params.append('limit', '200');
      
      const response = await api.get(`/platform-owner/vehicles?${params.toString()}`);
      setVehicles(response.data.vehicles || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied - Platform Owner only');
        navigate('/dashboard');
      } else {
        toast.error('Failed to fetch vehicles');
      }
    } finally {
      setLoading(false);
    }
  }, [api, filterStatus, searchQuery, includeArchivedTest, navigate]);

  useEffect(() => {
    fetchVehicles();
  }, [filterStatus, includeArchivedTest]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    fetchVehicles();
  };

  const fetchVehicleDetails = async (vehicleId) => {
    try {
      const response = await api.get(`/platform-owner/vehicles/${vehicleId}`);
      setVehicleDetails(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to fetch vehicle details');
    }
  };

  const handleHideVehicle = async (vehicleId) => {
    setActionLoading(vehicleId);
    try {
      await api.post(`/platform-owner/vehicles/${vehicleId}/hide`);
      toast.success('Vehicle hidden from public browse');
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to hide vehicle');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnhideVehicle = async (vehicleId) => {
    setActionLoading(vehicleId);
    try {
      await api.post(`/platform-owner/vehicles/${vehicleId}/unhide`);
      toast.success('Vehicle restored to active');
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unhide vehicle');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkTest = async (vehicleId) => {
    setActionLoading(vehicleId);
    try {
      await api.post(`/platform-owner/vehicles/${vehicleId}/mark-test`);
      toast.success('Vehicle marked as test data');
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as test');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteVehicle = async () => {
    if (!selectedVehicle || deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    setActionLoading(selectedVehicle.vehicle_id);
    try {
      await api.delete(`/platform-owner/vehicles/${selectedVehicle.vehicle_id}`);
      toast.success('Vehicle permanently deleted');
      setShowDeleteModal(false);
      setSelectedVehicle(null);
      setDeleteConfirmText('');
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete vehicle');
    } finally {
      setActionLoading(null);
    }
  };

  const openDeleteModal = (vehicle) => {
    setSelectedVehicle(vehicle);
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  const getStatusBadges = (vehicle) => {
    const badges = [];
    
    if (vehicle.is_active && !vehicle.test_data && !vehicle.created_by_seed && !vehicle.is_demo && !vehicle.hidden_from_browse) {
      badges.push({ label: 'ACTIVE', color: 'bg-green-500/20 text-green-400' });
    }
    
    if (vehicle.hidden_from_browse || vehicle.status === 'hidden' || vehicle.status === 'inactive') {
      badges.push({ label: 'HIDDEN', color: 'bg-orange-500/20 text-orange-400' });
    }
    
    if (vehicle.test_data || vehicle.created_by_seed || vehicle.is_demo) {
      badges.push({ label: 'TEST', color: 'bg-purple-500/20 text-purple-400' });
    }
    
    if (vehicle.is_orphaned) {
      badges.push({ label: 'ORPHAN', color: 'bg-red-500/20 text-red-400' });
    }
    
    if (vehicle.status === 'pending') {
      badges.push({ label: 'PENDING', color: 'bg-yellow-500/20 text-yellow-400' });
    }
    
    return badges;
  };

  const filterButtons = [
    { value: 'all', label: 'All', count: summary.total_all },
    { value: 'active', label: 'Active', count: summary.total_active },
    { value: 'hidden', label: 'Hidden', count: summary.total_hidden },
    { value: 'test', label: 'Test', count: summary.total_test },
    { value: 'orphaned', label: 'Orphaned', count: summary.total_orphaned },
  ];

  // Header actions for the shell
  const headerActions = (
    <Button 
      onClick={fetchVehicles}
      variant="outline" 
      className="border-white/20 text-white hover:bg-white/10"
      disabled={loading}
      data-testid="refresh-btn"
    >
      <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
      Refresh
    </Button>
  );

  return (
    <PlatformOwnerAppShell 
      title="Vehicle Management" 
      activeId="vehicle-mgmt"
      headerActions={headerActions}
    >
      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-6">
        {filterButtons.map((btn) => (
          <Card 
            key={btn.value}
            className={`cursor-pointer transition-all ${
              filterStatus === btn.value 
                  ? 'bg-purple-600/30 border-purple-500/50' 
                  : 'bg-white/5 border-white/10 hover:bg-white/10'
              }`}
              onClick={() => setFilterStatus(btn.value)}
              data-testid={`filter-${btn.value}`}
            >
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{btn.count ?? '-'}</p>
                <p className="text-xs text-white/60">{btn.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Search Bar */}
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  type="text"
                  placeholder="Search by vehicle ID, title, make, or model..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700" data-testid="search-btn">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </form>
            {/* Archived Test toggle — PO-only debug surface, default OFF */}
            <label
              className="mt-3 flex items-center gap-2 text-xs text-white/70 cursor-pointer select-none"
              data-testid="include-archived-test-toggle-label"
            >
              <input
                type="checkbox"
                checked={includeArchivedTest}
                onChange={(e) => setIncludeArchivedTest(e.target.checked)}
                className="accent-purple-500"
                data-testid="include-archived-test-toggle"
              />
              <span>
                Show archived test vehicles
                {summary.total_archived_test > 0 && (
                  <span
                    className="ml-1 text-white/40"
                    data-testid="archived-test-count-hint"
                  >({summary.total_archived_test} hidden)</span>
                )}
              </span>
            </label>
          </CardContent>
        </Card>

        {/* Vehicles Table */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader className="p-4 border-b border-white/10">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Car className="w-5 h-5 text-cyan-400" />
              All Vehicles ({vehicles.length})
              {filterStatus !== 'all' && (
                <Badge className="bg-purple-500/20 text-purple-300 ml-2">
                  Filter: {filterStatus}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500"></div>
              </div>
            ) : vehicles.length === 0 ? (
              <div className="text-center py-16">
                <Car className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <p className="text-white/60">No vehicles found</p>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10 text-left">
                        <th className="px-4 py-3 text-sm font-medium text-white/60">Vehicle</th>
                        <th className="px-4 py-3 text-sm font-medium text-white/60">Host</th>
                        <th className="px-4 py-3 text-sm font-medium text-white/60">Created</th>
                        <th className="px-4 py-3 text-sm font-medium text-white/60">Status</th>
                        <th className="px-4 py-3 text-sm font-medium text-white/60 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vehicles.map((vehicle) => (
                        <tr 
                          key={vehicle.vehicle_id} 
                          className="border-b border-white/5 hover:bg-white/5"
                          data-testid={`vehicle-row-${vehicle.vehicle_id}`}
                        >
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-sm">
                                {vehicle.title || `${vehicle.make} ${vehicle.model}`}
                              </p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span className="text-xs text-white/50">
                                  {vehicle.year} • {vehicle.make} {vehicle.model}
                                </span>
                                <Badge className="bg-blue-500/20 text-blue-300 text-[10px] capitalize" data-testid="vehicle-category-badge">
                                  {vehicle.vehicle_category === 'jetski' ? 'Jet Ski' : vehicle.vehicle_category === 'ebike' ? 'eBike' : (vehicle.vehicle_category || 'Car')}
                                  {vehicle.vehicle_category === 'car' && vehicle.vehicle_type ? ` — ${vehicle.vehicle_type}` : ''}
                                </Badge>
                              </div>
                              <p className="text-xs text-white/40 font-mono mt-1">
                                {vehicle.vehicle_id?.slice(0, 16)}...
                              </p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {vehicle.owner_email ? (
                              <div>
                                <p className="text-sm">{vehicle.owner_name || 'Unknown'}</p>
                                <p className="text-xs text-white/50">{vehicle.owner_email}</p>
                                {vehicle.owner_is_test && (
                                  <Badge className="bg-purple-500/20 text-purple-400 text-[10px] mt-1">Test User</Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-red-400 text-sm">No Owner (Orphan)</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-white/60">
                            {vehicle.created_at ? new Date(vehicle.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {getStatusBadges(vehicle).map((badge, i) => (
                                <Badge key={i} className={`${badge.color} text-xs`}>
                                  {badge.label}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-white/20 text-white hover:bg-white/10 h-8 text-xs"
                                onClick={() => fetchVehicleDetails(vehicle.vehicle_id)}
                                data-testid={`view-${vehicle.vehicle_id}`}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                View
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-white/20 text-white hover:bg-white/10 h-8 text-xs"
                                onClick={() => window.open(`/vehicles/${vehicle.vehicle_id}`, '_blank')}
                                data-testid={`listing-${vehicle.vehicle_id}`}
                              >
                                <ExternalLink className="w-3 h-3 mr-1" />
                                Listing
                              </Button>
                              {vehicle.hidden_from_browse || vehicle.status === 'hidden' ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-green-500/50 text-green-400 hover:bg-green-500/10 h-8 text-xs"
                                  onClick={() => handleUnhideVehicle(vehicle.vehicle_id)}
                                  disabled={actionLoading === vehicle.vehicle_id}
                                  data-testid={`activate-${vehicle.vehicle_id}`}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Activate
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10 h-8 text-xs"
                                  onClick={() => handleHideVehicle(vehicle.vehicle_id)}
                                  disabled={actionLoading === vehicle.vehicle_id}
                                  data-testid={`hide-${vehicle.vehicle_id}`}
                                >
                                  <EyeOff className="w-3 h-3 mr-1" />
                                  Hide
                                </Button>
                              )}
                              {!vehicle.test_data && !vehicle.created_by_seed && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 h-8 text-xs"
                                  onClick={() => handleMarkTest(vehicle.vehicle_id)}
                                  disabled={actionLoading === vehicle.vehicle_id}
                                  data-testid={`mark-test-${vehicle.vehicle_id}`}
                                >
                                  <TestTube className="w-3 h-3 mr-1" />
                                  Test
                                </Button>
                              )}
                              {(vehicle.test_data || vehicle.created_by_seed || vehicle.is_demo || vehicle.is_orphaned) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-8 text-xs"
                                  onClick={() => openDeleteModal(vehicle)}
                                  disabled={actionLoading === vehicle.vehicle_id}
                                  data-testid={`delete-${vehicle.vehicle_id}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden space-y-3 p-4">
                  {vehicles.map((vehicle) => (
                    <Card 
                      key={vehicle.vehicle_id} 
                      className="bg-white/5 border-white/10"
                      data-testid={`vehicle-card-${vehicle.vehicle_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">
                              {vehicle.title || `${vehicle.make} ${vehicle.model}`}
                            </p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <p className="text-xs text-white/50">{vehicle.year}</p>
                              <Badge className="bg-blue-500/20 text-blue-300 text-[10px] capitalize">
                                {vehicle.vehicle_category === 'jetski' ? 'Jet Ski' : vehicle.vehicle_category === 'ebike' ? 'eBike' : (vehicle.vehicle_category || 'Car')}
                                {vehicle.vehicle_category === 'car' && vehicle.vehicle_type ? ` — ${vehicle.vehicle_type}` : ''}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1 ml-2">
                            {getStatusBadges(vehicle).map((badge, i) => (
                              <Badge key={i} className={`${badge.color} text-[10px]`}>
                                {badge.label}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        
                        <div className="text-xs text-white/60 mb-3 space-y-1">
                          <p className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {vehicle.owner_email || 'No owner (orphan)'}
                          </p>
                          <p>Created: {vehicle.created_at ? new Date(vehicle.created_at).toLocaleDateString() : '-'}</p>
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-white/20 text-white hover:bg-white/10 h-8 text-xs flex-1"
                            onClick={() => fetchVehicleDetails(vehicle.vehicle_id)}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </Button>
                          {vehicle.hidden_from_browse || vehicle.status === 'hidden' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-green-500/50 text-green-400 hover:bg-green-500/10 h-8 text-xs flex-1"
                              onClick={() => handleUnhideVehicle(vehicle.vehicle_id)}
                              disabled={actionLoading === vehicle.vehicle_id}
                            >
                              Activate
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10 h-8 text-xs flex-1"
                              onClick={() => handleHideVehicle(vehicle.vehicle_id)}
                              disabled={actionLoading === vehicle.vehicle_id}
                            >
                              Hide
                            </Button>
                          )}
                          {(vehicle.test_data || vehicle.is_orphaned) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-8 text-xs"
                              onClick={() => openDeleteModal(vehicle)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Safety Notice */}
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-300">Public Visibility Rules</p>
                <p className="text-xs text-white/60 mt-1">
                  Vehicles with <code className="bg-white/10 px-1 rounded">test_data</code>, <code className="bg-white/10 px-1 rounded">created_by_seed</code>, or <code className="bg-white/10 px-1 rounded">is_demo</code> flags 
                  are automatically excluded from public vehicle browsing. They will always appear here in the Platform Owner view for management purposes.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

      {/* Vehicle Details Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="w-5 h-5 text-cyan-400" />
              Vehicle Details
            </DialogTitle>
          </DialogHeader>
          {vehicleDetails && (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-white/50">Title</p>
                  <p className="font-medium">{vehicleDetails.vehicle?.title || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-white/50">Make / Model / Year</p>
                  <p className="font-medium">
                    {vehicleDetails.vehicle?.make} {vehicleDetails.vehicle?.model} ({vehicleDetails.vehicle?.year})
                  </p>
                </div>
                <div>
                  <p className="text-xs text-white/50">Vehicle ID</p>
                  <p className="font-mono text-sm text-white/70">{vehicleDetails.vehicle?.vehicle_id}</p>
                </div>
                <div>
                  <p className="text-xs text-white/50">Status</p>
                  <p className="font-medium">{vehicleDetails.vehicle?.status}</p>
                </div>
                <div>
                  <p className="text-xs text-white/50">Daily Rate</p>
                  <p className="font-medium">{vehicleDetails.vehicle?.daily_rate ? `$${vehicleDetails.vehicle.daily_rate}/day` : 'Price not set'}</p>
                </div>
                <div>
                  <p className="text-xs text-white/50">Location</p>
                  <p className="font-medium">{vehicleDetails.vehicle?.location || '-'}</p>
                </div>
              </div>

              {/* Owner Info */}
              <div className="border-t border-white/10 pt-4">
                <p className="text-sm font-medium text-white/80 mb-2">Owner Information</p>
                {vehicleDetails.owner ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-white/50">Name</p>
                      <p>{vehicleDetails.owner.full_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-white/50">Email</p>
                      <p>{vehicleDetails.owner.email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-white/50">Phone</p>
                      <p>{vehicleDetails.owner.phone || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-white/50">Verification</p>
                      <p>{vehicleDetails.owner.verification_status || 'unverified'}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-red-400">No owner found (Orphaned Vehicle)</p>
                )}
              </div>

              {/* Stats */}
              <div className="border-t border-white/10 pt-4">
                <p className="text-sm font-medium text-white/80 mb-2">Statistics</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold">{vehicleDetails.stats?.total_bookings || 0}</p>
                    <p className="text-xs text-white/50">Total Bookings</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold">{vehicleDetails.stats?.completed_bookings || 0}</p>
                    <p className="text-xs text-white/50">Completed</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold">${vehicleDetails.stats?.total_revenue || 0}</p>
                    <p className="text-xs text-white/50">Revenue</p>
                  </div>
                </div>
              </div>

              {/* Flags */}
              <div className="border-t border-white/10 pt-4">
                <p className="text-sm font-medium text-white/80 mb-2">Flags</p>
                <div className="flex flex-wrap gap-2">
                  {vehicleDetails.flags?.test_data && (
                    <Badge className="bg-purple-500/20 text-purple-400">test_data = true</Badge>
                  )}
                  {vehicleDetails.flags?.created_by_seed && (
                    <Badge className="bg-purple-500/20 text-purple-400">created_by_seed = true</Badge>
                  )}
                  {vehicleDetails.flags?.is_demo && (
                    <Badge className="bg-purple-500/20 text-purple-400">is_demo = true</Badge>
                  )}
                  {vehicleDetails.flags?.hidden_from_browse && (
                    <Badge className="bg-orange-500/20 text-orange-400">hidden_from_browse = true</Badge>
                  )}
                  {vehicleDetails.is_orphaned && (
                    <Badge className="bg-red-500/20 text-red-400">Orphaned (No Owner)</Badge>
                  )}
                  {!vehicleDetails.flags?.test_data && !vehicleDetails.flags?.created_by_seed && 
                   !vehicleDetails.flags?.is_demo && !vehicleDetails.flags?.hidden_from_browse && 
                   !vehicleDetails.is_orphaned && (
                    <Badge className="bg-green-500/20 text-green-400">No special flags</Badge>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowDetailModal(false)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Close
            </Button>
            <Button 
              onClick={() => window.open(`/vehicles/${vehicleDetails?.vehicle?.vehicle_id}`, '_blank')}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View Public Listing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Delete Vehicle
            </DialogTitle>
            <DialogDescription className="text-white/60">
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {selectedVehicle && (
            <div className="space-y-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-sm font-medium mb-2">You are about to permanently delete:</p>
                <p className="text-white/80">{selectedVehicle.title || `${selectedVehicle.make} ${selectedVehicle.model}`}</p>
                <p className="text-xs text-white/50 font-mono mt-1">{selectedVehicle.vehicle_id}</p>
              </div>
              <div>
                <p className="text-sm text-white/60 mb-2">Type <strong>DELETE</strong> to confirm:</p>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="bg-white/10 border-white/20 text-white"
                  data-testid="delete-confirm-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteModal(false)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteVehicle}
              disabled={deleteConfirmText !== 'DELETE' || actionLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              {actionLoading ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PlatformOwnerAppShell>
  );
};

export default PlatformOwnerVehicleManagement;
